import { useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Icon } from "@/components/ui/Icon";
import { useUpdateWorkspace } from "@/hooks/useUpdateWorkspace";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { UUID } from "@/lib/types";

// Fixed company options. Free-form on the backend (workspaces.company); the
// dropdown just constrains the choices. Extend here if a new entity appears.
const COMPANY_OPTIONS = ["LLE", "LLT"] as const;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: UUID;
  workspaceName: string;
  currentCompany: string | null;
}

export function WorkspaceCompanyDialog({
  open,
  onOpenChange,
  workspaceId,
  workspaceName,
  currentCompany,
}: Props) {
  const update = useUpdateWorkspace();
  const [pending, setPending] = useState<string | null | "none">(null);

  async function pick(value: string | null) {
    setPending(value ?? "none");
    try {
      await update.mutateAsync({ workspaceId, payload: { company: value } });
      showToast(t.workspaceCompanySaved, "success");
      onOpenChange(false);
    } catch (err) {
      showToast(err instanceof Error ? err.message : t.unknownError, "error");
    } finally {
      setPending(null);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !update.isPending && onOpenChange(o)}
      title={t.workspaceCompanyTitle.replace("{name}", workspaceName)}
      description={t.workspaceCompanyDescription}
      width={420}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
        {COMPANY_OPTIONS.map((c) => (
          <OptionRow
            key={c}
            label={c}
            selected={currentCompany === c}
            busy={pending === c}
            disabled={update.isPending}
            onClick={() => pick(c)}
          />
        ))}
      </div>

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--hairline-soft)" }}>
        <OptionRow
          label={t.workspaceCompanyNone}
          hint={t.workspaceCompanyNoneHint}
          selected={currentCompany === null}
          busy={pending === "none"}
          disabled={update.isPending}
          onClick={() => pick(null)}
        />
      </div>
    </Dialog>
  );
}

function OptionRow({
  label,
  hint,
  selected,
  busy,
  disabled,
  onClick,
}: {
  label: string;
  hint?: string;
  selected: boolean;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        padding: "10px 12px",
        background: selected ? "var(--surface-soft)" : "transparent",
        border: `1px solid ${selected ? "var(--ink-1)" : "var(--hairline)"}`,
        borderRadius: 8,
        cursor: disabled ? "not-allowed" : busy ? "wait" : "pointer",
        fontSize: 13,
        color: "var(--ink-1)",
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
        textAlign: "left",
        transition: "background .12s, border-color .12s",
      }}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.background = "var(--surface-soft)";
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.background = "transparent";
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500 }}>{label}</div>
        {hint ? <div style={{ fontSize: 11.5, color: "var(--ink-3)" }}>{hint}</div> : null}
      </div>
      {selected ? <Icon name="check" size={14} color="var(--ink-1)" /> : null}
    </button>
  );
}
